import { spawn } from "node:child_process";

import { CliError, EXIT_OK, EXIT_RUNTIME, type CommandOutput } from "../common.js";
import { packageVersion } from "../package-root.js";

const UPDATE_PACKAGE = "@lingjingai/scriptctl";
const UPDATE_LATEST_SPEC = `${UPDATE_PACKAGE}@latest`;
const UPDATE_COMMAND = "npm";

function readInstalledVersion(): string {
  return packageVersion();
}

export function patchPackageSpec(version: string): string | null {
  const match = /^(\d+)\.(\d+)\.\d+/.exec(version);
  if (!match) return null;
  return `${UPDATE_PACKAGE}@~${match[1]}.${match[2]}.0`;
}

function updateArgs(packageSpec: string): readonly string[] {
  return ["install", "-g", packageSpec];
}

export interface UpdateRunResult {
  exitCode: number | null;
  signal: NodeJS.Signals | null;
  stdout: string;
  stderr: string;
  error?: Error;
}

export type UpdateRunner = (
  command: string,
  args: readonly string[],
) => Promise<UpdateRunResult>;

export type InstalledVersionReader = () => string;

function commandLine(command: string, args: readonly string[]): string {
  return [command, ...args].join(" ");
}

function nonEmptyTail(text: string, count: number): string[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  return lines.slice(-count);
}

export function runUpdateCommand(command: string, args: readonly string[]): Promise<UpdateRunResult> {
  return new Promise((resolve) => {
    const child = spawn(command, [...args], {
      stdio: ["ignore", "pipe", "pipe"],
    });
    const stdout: string[] = [];
    const stderr: string[] = [];
    child.stdout.setEncoding("utf-8");
    child.stderr.setEncoding("utf-8");
    child.stdout.on("data", (chunk: string) => stdout.push(chunk));
    child.stderr.on("data", (chunk: string) => stderr.push(chunk));
    child.once("error", (error) => {
      resolve({
        exitCode: null,
        signal: null,
        stdout: stdout.join(""),
        stderr: stderr.join(""),
        error,
      });
    });
    child.once("close", (exitCode, signal) => {
      resolve({
        exitCode,
        signal,
        stdout: stdout.join(""),
        stderr: stderr.join(""),
      });
    });
  });
}

export async function commandUpdate(
  opts: Record<string, unknown>,
  runner: UpdateRunner = runUpdateCommand,
  readVersion: InstalledVersionReader = readInstalledVersion,
): Promise<[CommandOutput, number]> {
  const patch = Boolean(opts["patch"]);
  const currentVersion = readVersion();
  const packageSpec = patch
    ? patchPackageSpec(currentVersion)
    : UPDATE_LATEST_SPEC;
  if (!packageSpec) {
    throw new CliError(`Cannot compute a patch update range from version ${currentVersion}.`, {
      exitCode: EXIT_RUNTIME,
      errorCode: "UPDATE_VERSION_INVALID",
      hint: "Run `scriptctl update` without --patch to install the latest version.",
      data: { version: currentVersion },
    });
  }
  const args = [...updateArgs(packageSpec)];
  const line = commandLine(UPDATE_COMMAND, args);
  const update = {
    package: UPDATE_PACKAGE,
    package_spec: packageSpec,
    command: UPDATE_COMMAND,
    args,
    command_line: line,
  };

  if (Boolean(opts["dry_run"])) {
    return [{
      text: [
        `package: ${UPDATE_PACKAGE}`,
        `command: ${line}`,
        "dry-run: no update performed",
      ].join("\n"),
      data: { op: "update", changed: false, update },
    }, EXIT_OK];
  }

  const result = await runner(UPDATE_COMMAND, args);
  if (result.error) {
    throw new CliError(`Failed to start npm: ${result.error.message}`, {
      exitCode: EXIT_RUNTIME,
      errorCode: "UPDATE_SPAWN_FAILED",
      hint: `Run '${line}' after fixing the npm installation.`,
      data: { update, cause: result.error.message },
    });
  }

  if (result.exitCode !== EXIT_OK) {
    const issues = [
      `exit: ${result.exitCode === null ? `signal ${result.signal ?? "unknown"}` : String(result.exitCode)}`,
      ...nonEmptyTail(result.stderr, 5).map((line) => `stderr: ${line}`),
    ];
    throw new CliError("npm could not update scriptctl.", {
      exitCode: EXIT_RUNTIME,
      errorCode: "UPDATE_COMMAND_FAILED",
      hint: `Run '${line}' after resolving the npm error.`,
      data: { update, actual: issues },
    });
  }

  const installedVersion = readVersion();
  const changed = installedVersion !== currentVersion;
  return [{
    text: changed
      ? `Updated ${currentVersion} → ${installedVersion}.`
      : `Already up to date (${installedVersion}).`,
    data: {
      op: "update",
      changed,
      status: changed ? "updated" : "already_latest",
      previous_version: currentVersion,
      version: installedVersion,
    },
  }, EXIT_OK];
}
